
import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { sanitizeText } from "@/utils/validation";
import { 
  Bold, 
  Italic, 
  Underline, 
  Strikethrough, 
  List, 
  ListOrdered,
  Type
} from "lucide-react";

interface RichTextEditorProps {
  value: string;
  onChange: (value: string, formatted: any) => void;
  placeholder?: string;
  rows?: number;
}

export function RichTextEditor({ value, onChange, placeholder, rows = 8 }: RichTextEditorProps) {
  const [textareaRef, setTextareaRef] = useState<HTMLTextAreaElement | null>(null);

  const getSelection = useCallback(() => {
    if (!textareaRef) return { start: 0, end: 0 };
    return {
      start: textareaRef.selectionStart,
      end: textareaRef.selectionEnd
    };
  }, [textareaRef]);

  const insertFormatting = useCallback((prefix: string, suffix: string = "") => {
    if (!textareaRef) return;
    
    const { start, end } = getSelection();
    const selectedText = value.substring(start, end);
    const beforeSelection = value.substring(0, start);
    const afterSelection = value.substring(end);
    
    const newText = `${beforeSelection}${prefix}${selectedText}${suffix}${afterSelection}`;
    
    // Sanitize the new text to prevent XSS
    const sanitized = sanitizeText(newText, 10000);
    onChange(sanitized, { formatted: true });
    
    // Focus back to textarea and set cursor position
    setTimeout(() => {
      textareaRef.focus();
      textareaRef.setSelectionRange(start + prefix.length, end + prefix.length);
    }, 0);
  }, [value, onChange, textareaRef, getSelection]);

  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    // Sanitize input as user types
    const sanitized = sanitizeText(e.target.value, 10000);
    onChange(sanitized, { formatted: true });
  }, [onChange]);

  const formatBold = () => insertFormatting("**", "**");
  const formatItalic = () => insertFormatting("*", "*");
  const formatUnderline = () => insertFormatting("<u>", "</u>");
  const formatStrikethrough = () => insertFormatting("~~", "~~");
  const formatBulletList = () => insertFormatting("\n- ", "");
  const formatNumberedList = () => insertFormatting("\n1. ", "");
  const formatHeading = () => insertFormatting("## ", "");

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-gray-50">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatBold}
          className="h-8 w-8 p-0"
        >
          <Bold className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatItalic}
          className="h-8 w-8 p-0"
        >
          <Italic className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatUnderline}
          className="h-8 w-8 p-0"
        >
          <Underline className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatStrikethrough}
          className="h-8 w-8 p-0"
        >
          <Strikethrough className="h-4 w-4" />
        </Button>
        <div className="w-px h-6 bg-gray-300 self-center" />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatBulletList}
          className="h-8 w-8 p-0"
        >
          <List className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatNumberedList}
          className="h-8 w-8 p-0"
        >
          <ListOrdered className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={formatHeading}
          className="h-8 w-8 p-0"
        >
          <Type className="h-4 w-4" />
        </Button>
      </div>
      <Textarea
        ref={setTextareaRef}
        value={value}
        onChange={handleChange}
        placeholder={placeholder}
        rows={rows}
        className="min-h-[200px]"
        maxLength={10000}
      />
    </div>
  );
}
